/**
 * Unified admin Site Manager.
 * Tabs:
 *   - Content    : edit hero, final CTA, brand explainer, footer copy, video, authority
 *   - Popup      : popup widget config (timing, exit-intent, copy, max-shows)
 *   - Lead Sources: bar chart + table of leads by source over rolling window
 *   - Email      : Resend webhook analytics (delivered/opened/clicked/bounced)
 *   - Backlog    : kanban-lite for future improvements (admin-curated only)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Save, BarChart3, Mail, Layers, ListChecks, Trash2, Plus, MessageSquare, CheckCircle, XCircle, Building2, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'content',      label: 'Content',          icon: Layers },
  { id: 'brands',       label: 'Brands',           icon: Building2 },
  { id: 'popup',        label: 'Popup Manager',    icon: Layers },
  { id: 'testimonials', label: 'Testimonials',     icon: MessageSquare },
  { id: 'leads',        label: 'Lead Sources',     icon: BarChart3 },
  { id: 'email',        label: 'Email Analytics',  icon: Mail },
  { id: 'backlog',      label: 'Backlog',          icon: ListChecks },
];

export default function AdminSiteManager() {
  const [tab, setTab] = useState('content');
  return (
    <AdminLayout title="Site Manager">
      <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
        <div className="flex flex-nowrap overflow-x-auto gap-1 border-b border-[#E2E8F0] px-3 pt-3 sm:flex-wrap sm:overflow-visible" data-testid="site-manager-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-t-md text-xs sm:text-sm font-bold transition-colors whitespace-nowrap ${active ? 'bg-[#1D4ED8] text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}`}
                data-testid={`site-manager-tab-${t.id}`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>
        <div className="p-3 sm:p-6">
          {tab === 'content'      && <ContentTab />}
          {tab === 'brands'       && <BrandsTab />}
          {tab === 'popup'        && <PopupTab />}
          {tab === 'testimonials' && <TestimonialsTab />}
          {tab === 'leads'        && <LeadSourcesTab />}
          {tab === 'email'        && <EmailAnalyticsTab />}
          {tab === 'backlog'      && <BacklogTab />}
        </div>
      </div>
    </AdminLayout>
  );
}

// ============ CONTENT TAB ============

function ContentTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/site/settings`, { withCredentials: true })
      .then((r) => setSettings(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load settings'));
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API_URL}/api/admin/site/settings`, patch, { withCredentials: true });
      setSettings(r.data);
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-sm text-[#94A3B8]">Loading...</div>;

  return (
    <div className="space-y-6">
      <SectionCard
        title="Hero Section"
        testid="content-hero"
        onSave={(payload) => save({ hero: payload })}
        saving={saving}
        initial={settings.hero}
        previewRender={(form) => <HeroPreview hero={form} />}
      >
        {(form, setForm) => (
          <>
            <Field label="Overline (tag above headline)" v={form.overline} onChange={(v) => setForm({ ...form, overline: v })} />
            <Field label="Headline part A" v={form.headline_a} onChange={(v) => setForm({ ...form, headline_a: v })} />
            <Field label="Headline part B (blue accent)" v={form.headline_b} onChange={(v) => setForm({ ...form, headline_b: v })} />
            <Field label="Headline part C (orange accent)" v={form.headline_c} onChange={(v) => setForm({ ...form, headline_c: v })} />
            <Field label="Sub-headline" v={form.sub} onChange={(v) => setForm({ ...form, sub: v })} textarea />
            <Field label="Primary CTA label" v={form.primary_cta_label} onChange={(v) => setForm({ ...form, primary_cta_label: v })} />
            <Field label="Primary CTA link" v={form.primary_cta_link} onChange={(v) => setForm({ ...form, primary_cta_link: v })} />
            <Field label="Secondary CTA label" v={form.secondary_cta_label} onChange={(v) => setForm({ ...form, secondary_cta_label: v })} />
            <Field label="Secondary CTA link" v={form.secondary_cta_link} onChange={(v) => setForm({ ...form, secondary_cta_link: v })} />
            <ImageUploadField label="Hero image" v={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="content-hero-image" />
          </>
        )}
      </SectionCard>

      <SectionCard
        title="Final CTA Section"
        testid="content-final-cta"
        onSave={(payload) => save({ final_cta: payload })}
        saving={saving}
        initial={settings.final_cta}
        previewRender={(form) => <FinalCtaPreview cta={form} />}
      >
        {(form, setForm) => (
          <>
            <Field label="Urgency line (orange tag)" v={form.urgency_line} onChange={(v) => setForm({ ...form, urgency_line: v })} />
            <Field label="Headline" v={form.headline} onChange={(v) => setForm({ ...form, headline: v })} />
            <Field label="Body" v={form.body} onChange={(v) => setForm({ ...form, body: v })} textarea />
            <Field label="Sub-body" v={form.sub_body} onChange={(v) => setForm({ ...form, sub_body: v })} textarea />
            <Field label="Primary CTA label" v={form.primary_cta_label} onChange={(v) => setForm({ ...form, primary_cta_label: v })} />
            <Field label="Primary CTA link" v={form.primary_cta_link} onChange={(v) => setForm({ ...form, primary_cta_link: v })} />
          </>
        )}
      </SectionCard>

      <SectionCard
        title="Brand Page Explainer"
        testid="content-brand"
        onSave={(payload) => save({ brand_explainer: payload.brand_explainer })}
        saving={saving}
        initial={{ brand_explainer: settings.brand_explainer }}
        previewRender={(form) => <BrandExplainerPreview text={form.brand_explainer} />}
      >
        {(form, setForm) => (
          <Field label="Brand ecosystem statement" v={form.brand_explainer} onChange={(v) => setForm({ ...form, brand_explainer: v })} textarea />
        )}
      </SectionCard>

      <SectionCard
        title="Footer Newsletter Copy"
        testid="content-footer"
        onSave={(payload) => save({ footer_newsletter: payload })}
        saving={saving}
        initial={settings.footer_newsletter}
        previewRender={(form) => <FooterPreview footer={form} />}
      >
        {(form, setForm) => (
          <>
            <Field label="Overline" v={form.overline} onChange={(v) => setForm({ ...form, overline: v })} />
            <Field label="Headline" v={form.headline} onChange={(v) => setForm({ ...form, headline: v })} />
            <Field label="Body" v={form.body} onChange={(v) => setForm({ ...form, body: v })} textarea />
          </>
        )}
      </SectionCard>

      <SectionCard
        title="Signature Visual Moment (hidden until enabled)"
        testid="content-video"
        onSave={(payload) => save({ video_section: payload })}
        saving={saving}
        initial={settings.video_section}
        previewRender={(form) => <VideoPreview video={form} />}
      >
        {(form, setForm) => (
          <>
            <label className="flex items-center gap-2 text-sm font-bold text-[#0A0F1C]">
              <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              Show this section on homepage
            </label>
            <Field label="Heading" v={form.heading} onChange={(v) => setForm({ ...form, heading: v })} />
            <Field label="Sub-heading" v={form.sub} onChange={(v) => setForm({ ...form, sub: v })} textarea />
            <Field label="Video URL (YouTube/Vimeo embed)" v={form.video_url} onChange={(v) => setForm({ ...form, video_url: v })} />
            <ImageUploadField label="Image fallback (shown if no video)" v={form.image_fallback_url} onChange={(v) => setForm({ ...form, image_fallback_url: v })} testid="content-video-image" />
            <div>
              <div className="text-xs font-bold text-[#475569] uppercase tracking-wider mb-1">Display style</div>
              <select className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" value={form.display_style || 'video_feature'} onChange={(e) => setForm({ ...form, display_style: e.target.value })}>
                <option value="video_feature">Video feature</option>
                <option value="founder_spotlight">Founder spotlight</option>
                <option value="workshop_montage">Workshop montage</option>
                <option value="framework_animation">Framework animation</option>
                <option value="ecosystem_map">Ecosystem map</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA label" v={form.cta_label} onChange={(v) => setForm({ ...form, cta_label: v })} />
              <Field label="CTA link" v={form.cta_link} onChange={(v) => setForm({ ...form, cta_link: v })} />
            </div>
          </>
        )}
      </SectionCard>

      <SectionCard
        title="Featured In / As Seen In (CMS-editable)"
        testid="content-featured-in"
        onSave={(payload) => save({ featured_in: payload })}
        saving={saving}
        initial={settings.featured_in}
        previewRender={(form) => <FeaturedInPreview data={form} />}
      >
        {(form, setForm) => {
          const logos = Array.isArray(form.logos) ? form.logos : [];
          const updateLogo = (idx, patch) => {
            const next = logos.map((l, i) => (i === idx ? { ...l, ...patch } : l));
            setForm({ ...form, logos: next });
          };
          const addLogo = () => setForm({ ...form, logos: [...logos, { label: '', logo_url: '', link: '' }] });
          const removeLogo = (idx) => setForm({ ...form, logos: logos.filter((_, i) => i !== idx) });
          return (
            <>
              <label className="flex items-center gap-2 text-sm font-bold text-[#0A0F1C]">
                <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="featured-in-enabled" />
                Show "Featured In" strip on the homepage
              </label>
              <p className="text-xs text-[#94A3B8]">Stays hidden until you enable it AND add at least one logo. Use a label-only entry if you don't have a logo image yet.</p>
              <Field label="Overline" v={form.overline} onChange={(v) => setForm({ ...form, overline: v })} />
              <Field label="Headline" v={form.headline} onChange={(v) => setForm({ ...form, headline: v })} />
              <div className="space-y-3">
                {logos.length === 0 && (
                  <div className="text-xs text-[#94A3B8] italic">No logos yet. Click "Add logo" below.</div>
                )}
                {logos.map((logo, idx) => (
                  <div key={idx} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 space-y-2" data-testid={`featured-logo-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold uppercase tracking-wider text-[#475569]">Logo {idx + 1}</div>
                      <button type="button" onClick={() => removeLogo(idx)} className="text-[#94A3B8] hover:text-red-600" aria-label="Remove logo" data-testid={`featured-logo-remove-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <Field label="Label (fallback if no image)" v={logo.label} onChange={(v) => updateLogo(idx, { label: v })} />
                    <ImageUploadField label="Logo image" v={logo.logo_url} onChange={(v) => updateLogo(idx, { logo_url: v })} testid={`featured-logo-img-${idx}`} />
                    <Field label="Click-through URL (optional)" v={logo.link} onChange={(v) => updateLogo(idx, { link: v })} />
                  </div>
                ))}
                <button type="button" onClick={addLogo} className="inline-flex items-center gap-2 text-sm font-bold text-[#1D4ED8] hover:text-[#1E40AF]" data-testid="featured-logo-add">
                  <Plus size={14} /> Add logo
                </button>
              </div>
            </>
          );
        }}
      </SectionCard>

      <SectionCard
        title="Founder Images (placeholders for future use)"
        testid="content-founder-images"
        onSave={(payload) => save({ founder_images: payload })}
        saving={saving}
        initial={settings.founder_images}
      >
        {(form, setForm) => (
          <>
            <p className="text-xs text-[#94A3B8]">These remain hidden across the site until you upload an image. Use them strategically so the same workshop photo is not repeated everywhere.</p>
            <ImageUploadField label="Founder headshot" v={form.headshot_url} onChange={(v) => setForm({ ...form, headshot_url: v })} testid="img-headshot" />
            <ImageUploadField label="Workshop photo" v={form.workshop_url} onChange={(v) => setForm({ ...form, workshop_url: v })} testid="img-workshop" />
            <ImageUploadField label="Speaking photo" v={form.speaking_url} onChange={(v) => setForm({ ...form, speaking_url: v })} testid="img-speaking" />
            <ImageUploadField label="Client session photo" v={form.client_session_url} onChange={(v) => setForm({ ...form, client_session_url: v })} testid="img-client" />
            <ImageUploadField label="Behind-the-scenes photo" v={form.behind_scenes_url} onChange={(v) => setForm({ ...form, behind_scenes_url: v })} testid="img-bts" />
            <ImageUploadField label="Event photo" v={form.event_url} onChange={(v) => setForm({ ...form, event_url: v })} testid="img-event" />
          </>
        )}
      </SectionCard>
    </div>
  );
}

// ============ POPUP TAB ============

function PopupTab() {
  const [popup, setPopup] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/site/settings`, { withCredentials: true })
      .then((r) => setPopup(r.data.popup))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load'));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API_URL}/api/admin/site/settings`, { popup }, { withCredentials: true });
      setPopup(r.data.popup);
      toast.success('Popup config saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!popup) return <div className="text-sm text-[#94A3B8]">Loading...</div>;
  const set = (k, v) => setPopup({ ...popup, [k]: v });

  return (
      <div className="space-y-4 max-w-5xl" data-testid="popup-manager">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7 space-y-4">
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5 space-y-4">
        <label className="flex items-center gap-2 text-sm font-bold text-[#0A0F1C]">
          <input type="checkbox" checked={popup.enabled !== false} onChange={(e) => set('enabled', e.target.checked)} data-testid="popup-enabled" />
          Popup is enabled site-wide
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Time trigger (seconds)" type="number" v={popup.trigger_delay_seconds} onChange={(v) => set('trigger_delay_seconds', Number(v) || 0)} />
          <Field label="Scroll trigger (% of page)" type="number" v={popup.trigger_scroll_percent} onChange={(v) => set('trigger_scroll_percent', Number(v) || 0)} />
          <Field label="Min delay after load (sec)" type="number" v={popup.min_delay_seconds} onChange={(v) => set('min_delay_seconds', Number(v) || 0)} />
          <Field label="Max shows per session" type="number" v={popup.max_shows_per_session} onChange={(v) => set('max_shows_per_session', Number(v) || 1)} />
        </div>
        <label className="flex items-center gap-2 text-sm font-bold text-[#0A0F1C]">
          <input type="checkbox" checked={popup.exit_intent_enabled !== false} onChange={(e) => set('exit_intent_enabled', e.target.checked)} data-testid="popup-exit-intent" />
          Exit-intent trigger on desktop (when user moves toward closing tab)
        </label>
        <div>
          <div className="text-xs font-bold text-[#475569] uppercase tracking-wider mb-1">Exit-intent style</div>
          <select className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" value={popup.exit_intent_style || 'center_modal'} onChange={(e) => set('exit_intent_style', e.target.value)} data-testid="popup-exit-style">
            <option value="center_modal">Center modal (bigger, with dimmed backdrop)</option>
            <option value="slide_in">Slide-in (small bottom-right card)</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm font-bold text-[#0A0F1C]">
          <input type="checkbox" checked={!!popup.suppress_on_mobile} onChange={(e) => set('suppress_on_mobile', e.target.checked)} />
          Suppress popup on mobile viewports
        </label>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        <h3 className="font-bold text-[#0A0F1C]">Copy & Offer</h3>
        <Field label="Headline" v={popup.headline} onChange={(v) => set('headline', v)} />
        <Field label="Sub-headline" v={popup.sub_headline} onChange={(v) => set('sub_headline', v)} textarea />
        <Field label="CTA button label" v={popup.cta_label} onChange={(v) => set('cta_label', v)} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Resource ID" v={popup.resource_id} onChange={(v) => set('resource_id', v)} />
          <Field label="Resource name" v={popup.resource_name} onChange={(v) => set('resource_name', v)} />
        </div>
      </div>
      <button onClick={save} disabled={saving} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold text-sm px-6 py-3 rounded-md inline-flex items-center gap-2 disabled:opacity-60" data-testid="popup-save">
        <Save size={14} /> {saving ? 'Saving...' : 'Save Popup Config'}
      </button>
          </div>
          <div className="lg:col-span-5">
            <div className="sticky top-4">
              <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#94A3B8] mb-2">Live preview</div>
              <PopupPreview popup={popup} />
            </div>
          </div>
        </div>
      </div>
    );
  }

function PopupPreview({ popup = {} }) {
  return (
    <div className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-lg p-4 min-h-[400px] relative" data-testid="popup-preview">
      <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#475569] mb-3 text-center">{popup.exit_intent_style === 'center_modal' ? 'Center-modal preview (exit-intent)' : 'Slide-in preview (timer / scroll)'}</div>
      <div className="bg-white shadow-2xl border border-[#E2E8F0] rounded-2xl overflow-hidden max-w-[360px] mx-auto">
        <div className="h-1.5 bg-[#1D4ED8]" />
        <div className="px-5 pt-5 pb-5 relative">
          <div className="absolute top-3 right-3 text-[#94A3B8]"><span className="text-xs">×</span></div>
          <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#F97316] mb-1">{popup.exit_intent_style === 'center_modal' ? 'Wait! Before you go' : 'FREE Download'}</div>
          <h3 className="font-bold text-[#0A0F1C] text-base leading-snug mb-1">{popup.headline || 'Headline'}</h3>
          <p className="text-xs text-[#475569] leading-relaxed mb-3">{popup.sub_headline || 'Sub-headline...'}</p>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-[#E2E8F0] rounded px-2 py-1.5 text-[10px] text-[#94A3B8]">First name</div>
              <div className="border border-[#E2E8F0] rounded px-2 py-1.5 text-[10px] text-[#94A3B8]">Last name</div>
            </div>
            <div className="border border-[#E2E8F0] rounded px-2 py-1.5 text-[10px] text-[#94A3B8]">Email</div>
            <div className="border border-[#E2E8F0] rounded px-2 py-1.5 text-[10px] text-[#94A3B8]">Phone (optional)</div>
            <div className="bg-[#F97316] text-white text-[11px] font-bold rounded px-3 py-2 text-center">{popup.cta_label || 'CTA'}</div>
            <div className="text-[9px] text-[#94A3B8] text-center pt-0.5">No spam. Unsubscribe anytime.</div>
          </div>
        </div>
      </div>
      <div className="mt-4 text-[10px] text-[#94A3B8] text-center">
        Triggers after <strong>{popup.trigger_delay_seconds || 0}s</strong> or <strong>{popup.trigger_scroll_percent || 0}%</strong> scroll · max <strong>{popup.max_shows_per_session || 1}</strong> show{(popup.max_shows_per_session || 1) === 1 ? '' : 's'}/session · exit-intent <strong>{popup.exit_intent_enabled !== false ? 'ON' : 'OFF'}</strong>{popup.suppress_on_mobile ? ' · mobile suppressed' : ''}
      </div>
    </div>
  );
}

// ============ LEAD SOURCES TAB ============

function LeadSourcesTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/admin/analytics/lead-sources?days=${days}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load lead sources'));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-[#94A3B8]">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="lead-sources">
      <div className="flex items-center gap-3">
        <label className="text-sm font-bold text-[#475569]">Window:</label>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="px-3 py-1.5 border border-[#E2E8F0] rounded-md text-sm" data-testid="lead-sources-window">
          {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
        </select>
        <div className="ml-auto text-sm text-[#475569]"><strong className="text-[#0A0F1C]">{data.total_leads}</strong> total leads</div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {data.by_source.length === 0 && <div className="lg:col-span-3 text-sm text-[#94A3B8]">No leads in this window yet.</div>}
        {data.by_source.map((s) => (
          <div key={s.source} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-[#1D4ED8] mb-1">{s.source}</div>
            <div className="text-3xl font-bold text-[#0A0F1C]">{s.count}</div>
            <div className="text-xs text-[#94A3B8]">leads in last {days} days</div>
          </div>
        ))}
      </div>
      {data.by_day.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="font-bold text-[#0A0F1C] mb-3">Daily breakdown</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#F8FAFC]">
                <tr>
                  <th className="text-left px-2 py-2 font-bold text-[#475569]">Date</th>
                  {data.sources_sorted.map((s) => <th key={s} className="text-right px-2 py-2 font-bold text-[#475569]">{s}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.by_day.map((row) => (
                  <tr key={row.date} className="border-t border-[#E2E8F0]">
                    <td className="px-2 py-1.5 font-mono text-[#0A0F1C]">{row.date}</td>
                    {data.sources_sorted.map((s) => <td key={s} className="px-2 py-1.5 text-right text-[#0A0F1C]">{row.counts[s] || 0}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ EMAIL ANALYTICS TAB ============

function EmailAnalyticsTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/analytics/email?days=${days}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load email analytics'));
  }, [days]);

  if (!data) return <div className="text-sm text-[#94A3B8]">Loading...</div>;
  const T = data.totals;
  const R = data.rates;

  return (
    <div className="space-y-6" data-testid="email-analytics">
      {!data.configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          Resend API key not configured. Set <code>RESEND_API_KEY</code> + add a webhook in the Resend dashboard pointing to <code>/api/webhooks/resend</code> to start collecting analytics.
        </div>
      )}
      <div className="flex items-center gap-3">
        <label className="text-sm font-bold text-[#475569]">Window:</label>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="px-3 py-1.5 border border-[#E2E8F0] rounded-md text-sm">
          {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Sent" v={T.sent} />
        <Stat label="Delivered" v={T.delivered} />
        <Stat label="Opened" v={T.opened} extra={R.open_rate + '%'} />
        <Stat label="Clicked" v={T.clicked} extra={R.click_rate + '%'} />
        <Stat label="Bounced" v={T.bounced} extra={R.bounce_rate + '%'} accent="red" />
        <Stat label="Complained" v={T.complained} accent="red" />
      </div>
      {data.recent.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="font-bold text-[#0A0F1C] mb-3">Recent events ({data.recent.length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#F8FAFC]">
                <tr>
                  <th className="text-left px-2 py-2 font-bold text-[#475569]">When</th>
                  <th className="text-left px-2 py-2 font-bold text-[#475569]">Event</th>
                  <th className="text-left px-2 py-2 font-bold text-[#475569]">To</th>
                  <th className="text-left px-2 py-2 font-bold text-[#475569]">Subject</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((row) => (
                  <tr key={(row.email_id || '') + row.created_at} className="border-t border-[#E2E8F0]">
                    <td className="px-2 py-1.5 font-mono text-[#0A0F1C]">{(row.created_at || '').slice(0, 19)}</td>
                    <td className="px-2 py-1.5 text-[#0A0F1C]">{row.event_type}</td>
                    <td className="px-2 py-1.5 text-[#0A0F1C]">{row.to || ''}</td>
                    <td className="px-2 py-1.5 text-[#475569]">{row.subject || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ BACKLOG TAB ============

function BacklogTab() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: '', notes: '', priority: 'P2' });

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/admin/backlog`, { withCredentials: true })
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load backlog'));
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/admin/backlog`, form, { withCredentials: true });
      setForm({ title: '', notes: '', priority: 'P2' });
      toast.success('Backlog item added');
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Add failed');
    } finally {
      setAdding(false);
    }
  };

  const setStatus = async (it, status) => {
    try {
      await axios.patch(`${API_URL}/api/admin/backlog/${it.item_id}`, { title: it.title, status }, { withCredentials: true });
      load();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const del = async (it) => {
    if (!window.confirm('Delete this backlog item?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/backlog/${it.item_id}`, { withCredentials: true });
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="backlog">
      <form onSubmit={add} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end" data-testid="backlog-add-form">
        <div className="md:col-span-3">
          <Field label="Title" v={form.title} onChange={(v) => setForm({ ...form, title: v })} />
        </div>
        <div className="md:col-span-2">
          <Field label="Notes" v={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
        </div>
        <div>
          <div className="text-xs font-bold text-[#475569] uppercase tracking-wider mb-1">Priority</div>
          <select className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            <option value="P0">P0</option><option value="P1">P1</option><option value="P2">P2</option><option value="P3">P3</option>
          </select>
        </div>
        <button type="submit" disabled={adding} className="md:col-span-6 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold text-sm py-2.5 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="backlog-add-submit">
          <Plus size={14} /> Add backlog item
        </button>
      </form>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {['open', 'in_progress', 'done'].map((col) => (
          <div key={col} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-3 min-h-[200px]" data-testid={`backlog-col-${col}`}>
            <div className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-3">{col.replace('_', ' ')}</div>
            {items.filter((it) => (it.status || 'open') === col).map((it) => (
              <div key={it.item_id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 mb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-bold text-sm text-[#0A0F1C]">{it.title}</div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8]">{it.priority}</span>
                </div>
                {it.notes && <div className="text-xs text-[#475569] mt-1">{it.notes}</div>}
                <div className="flex items-center justify-between gap-2 mt-2">
                  <select value={col} onChange={(e) => setStatus(it, e.target.value)} className="text-xs border border-[#E2E8F0] rounded px-1 py-0.5">
                    <option value="open">open</option><option value="in_progress">in_progress</option><option value="done">done</option>
                  </select>
                  <button onClick={() => del(it)} className="text-[#94A3B8] hover:text-red-600" aria-label="Delete"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ Helpers ============

// ============ BRANDS TAB ============

const EMPTY_BRAND = { name: '', slug: '', category: '', website_url: '', description: '', order: 0, is_active: true };

function BrandsTab() {
  const [brands, setBrands] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_BRAND);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/brands`, { withCredentials: true });
      setBrands(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load brands');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    const payload = { ...form, slug: form.slug.trim() || slugify(form.name), order: Number(form.order) || 0 };
    if (!payload.category.trim()) return toast.error('Category is required');
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/admin/brands`, payload, { withCredentials: true });
      toast.success('Brand added');
      setForm(EMPTY_BRAND);
      await load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Add failed');
    } finally {
      setAdding(false);
    }
  };

  if (!brands) return <div className="text-sm text-[#94A3B8]">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="brands-tab">
      <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-4 text-sm text-[#1E40AF]" data-testid="brands-deploy-note">
        These edits update the live site (Brands page + homepage) immediately in this environment. To push them to your
        deployed site, open <strong>Deploy Readiness → Refresh snapshots</strong>, then redeploy.
      </div>

      {/* Add new brand */}
      <form onSubmit={add} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4 space-y-3" data-testid="brand-add-form">
        <h3 className="font-bold text-[#0A0F1C] text-sm">Add a new brand</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Category" v={form.category} onChange={(v) => setForm({ ...form, category: v })} />
          <Field label="Website URL" v={form.website_url} onChange={(v) => setForm({ ...form, website_url: v })} />
        </div>
        <Field label="Description" v={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <Field label="Slug (auto if blank)" v={form.slug} onChange={(v) => setForm({ ...form, slug: v })} />
          <Field label="Order" type="number" v={form.order} onChange={(v) => setForm({ ...form, order: v })} />
          <button type="submit" disabled={adding} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold text-sm py-2.5 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="brand-add-submit">
            <Plus size={14} /> {adding ? 'Adding...' : 'Add brand'}
          </button>
        </div>
      </form>

      {/* Existing brands */}
      <div className="space-y-3">
        <div className="text-xs font-bold uppercase tracking-wider text-[#475569]">{brands.length} brand{brands.length === 1 ? '' : 's'}</div>
        {brands.map((b) => (
          <BrandRow key={b.brand_id} brand={b} onChanged={load} />
        ))}
      </div>
    </div>
  );
}

function BrandRow({ brand, onChanged }) {
  const [form, setForm] = useState(brand);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const dirty = JSON.stringify(form) !== JSON.stringify(brand);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/admin/brands/${brand.brand_id}`, {
        name: form.name, slug: form.slug, category: form.category,
        website_url: form.website_url || null, description: form.description,
        order: Number(form.order) || 0, is_active: !!form.is_active,
      }, { withCredentials: true });
      toast.success('Brand saved');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${brand.name}"? This removes it from the Brands page and homepage.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/brands/${brand.brand_id}`, { withCredentials: true });
      toast.success('Brand deleted');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={`brand-row-${form.slug}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="font-bold text-[#0A0F1C] truncate">{form.name || 'Untitled brand'}</h3>
          {form.website_url && (
            <a href={form.website_url} target="_blank" rel="noreferrer" className="text-[#1D4ED8] hover:text-[#1E40AF]" aria-label="Open website">
              <ExternalLink size={13} />
            </a>
          )}
          {!form.is_active && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#F1F5F9] text-[#94A3B8] px-2 py-0.5 rounded-full">Hidden</span>}
        </div>
        <button onClick={del} disabled={deleting} className="text-[#94A3B8] hover:text-red-600 shrink-0" aria-label="Delete brand" data-testid={`brand-delete-${form.slug}`}>
          <Trash2 size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <Field label="Category" v={form.category} onChange={(v) => setForm({ ...form, category: v })} />
        <Field label="Website URL" v={form.website_url} onChange={(v) => setForm({ ...form, website_url: v })} />
      </div>
      <div className="mt-3">
        <Field label="Description" v={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mt-3">
        <Field label="Slug" v={form.slug} onChange={(v) => setForm({ ...form, slug: v })} />
        <Field label="Order" type="number" v={form.order} onChange={(v) => setForm({ ...form, order: v })} />
        <label className="flex items-center gap-2 text-sm font-bold text-[#0A0F1C] pb-2">
          <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid={`brand-active-${form.slug}`} />
          Visible on site
        </label>
      </div>
      <div className="mt-3">
        <button onClick={save} disabled={saving || !dirty} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold text-sm px-5 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-50" data-testid={`brand-save-${form.slug}`}>
          <Save size={14} /> {saving ? 'Saving...' : dirty ? 'Save changes' : 'Saved'}
        </button>
      </div>
    </div>
  );
}

// ============ TESTIMONIALS TAB ============

function TestimonialsTab() {
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/testimonial-submissions${filter ? `?status=${filter}` : ''}`, { withCredentials: true });
      setSubmissions(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await axios.post(`${API_URL}/api/admin/testimonial-submissions/${id}/${action}`, {}, { withCredentials: true });
      toast.success(action === 'approve' ? 'Approved and published' : 'Rejected');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="testimonials-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-[#0A0F1C]">Public Testimonial Submissions</h2>
          <p className="text-sm text-[#475569]">Approve or reject testimonials clients submitted via <code className="bg-[#F1F5F9] px-1 rounded">/share-your-story</code>.</p>
        </div>
        <div className="flex items-center gap-1 bg-[#F1F5F9] rounded-md p-1">
          {['pending', 'approved', 'rejected', ''].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs font-bold rounded-md ${filter === s ? 'bg-white text-[#1D4ED8] shadow-sm' : 'text-[#475569]'}`}
              data-testid={`testimonials-filter-${s || 'all'}`}
            >
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#94A3B8] text-sm">Loading…</div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-12 text-[#94A3B8] text-sm" data-testid="testimonials-empty">
          No {filter || 'submissions'} testimonials yet.
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map((s) => (
            <div key={s.submission_id} className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid={`testimonial-sub-${s.submission_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-[#0A0F1C]">{s.name}</h3>
                    {s.title && <span className="text-xs text-[#475569]">· {s.title}</span>}
                    {s.company && <span className="text-xs text-[#475569]">at {s.company}</span>}
                  </div>
                  {s.result_badge && (
                    <div className="inline-block mt-1 text-[10px] font-bold tracking-wider uppercase text-[#F97316] border border-[#FED7AA] bg-[#FFF7ED] px-2 py-0.5 rounded">{s.result_badge}</div>
                  )}
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : s.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{s.status}</span>
              </div>
              <p className="text-sm text-[#0A0F1C] leading-relaxed mb-3">"{s.quote}"</p>
              <div className="flex items-center justify-between text-[11px] text-[#94A3B8]">
                <span>{s.email || 'no email'} · {s.submitted_at?.slice(0, 10)}</span>
                {s.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => act(s.submission_id, 'approve')}
                      disabled={busy === s.submission_id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-md disabled:opacity-50"
                      data-testid={`approve-sub-${s.submission_id}`}
                    >
                      <CheckCircle size={12} /> Approve & Publish
                    </button>
                    <button
                      onClick={() => act(s.submission_id, 'reject')}
                      disabled={busy === s.submission_id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-red-50 text-red-600 border border-red-200 text-xs font-bold rounded-md disabled:opacity-50"
                      data-testid={`reject-sub-${s.submission_id}`}
                    >
                      <XCircle size={12} /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function ImageUploadField({ label, v, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await axios.post(`${API_URL}/api/admin/site/upload-image`, fd, { withCredentials: true });
      onChange(r.data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  return (
    <div data-testid={testid}>
      <div className="text-xs font-bold text-[#475569] uppercase tracking-wider mb-1">{label}</div>
      <div className="flex items-center gap-3">
        {v ? (
          <img src={v.startsWith('http') ? v : `${API_URL}${v}`} alt="" className="w-24 h-24 object-cover rounded-md border border-[#E2E8F0]" />
        ) : (
          <div className="w-24 h-24 bg-[#F8FAFC] border border-dashed border-[#E2E8F0] rounded-md flex items-center justify-center text-xs text-[#94A3B8]">No image</div>
        )}
        <div className="flex-1 space-y-1">
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" ref={fileRef} onChange={handleFile} className="text-xs" data-testid={`${testid}-input`} />
          <input type="text" value={v || ''} onChange={(e) => onChange(e.target.value)} placeholder="Or paste image URL" className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded-md text-xs" />
          {uploading && <div className="text-xs text-[#1D4ED8]">Uploading...</div>}
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, children, onSave, saving, initial, testid, previewRender }) {
  const [form, setForm] = useState(initial || {});
  useEffect(() => { setForm(initial || {}); }, [initial]);
  const hasPreview = typeof previewRender === 'function';
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={testid}>
      <h3 className="font-bold text-[#0A0F1C] mb-3">{title}</h3>
      <div className={hasPreview ? 'grid grid-cols-1 lg:grid-cols-12 gap-5' : ''}>
        <div className={hasPreview ? 'lg:col-span-7 space-y-3' : 'space-y-3'}>
          {typeof children === 'function' ? children(form, setForm) : children}
          <button onClick={() => onSave(form)} disabled={saving} className="mt-2 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-bold text-sm px-5 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-60" data-testid={`${testid}-save`}>
            <Save size={14} /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
        {hasPreview && (
          <div className="lg:col-span-5">
            <div className="sticky top-4">
              <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#94A3B8] mb-2">Live preview</div>
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`${testid}-preview`}>
                {previewRender(form)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Live preview components ============

function HeroPreview({ hero = {} }) {
  return (
    <div className="bg-white p-4 text-left">
      {hero.overline && <div className="text-[9px] font-bold tracking-[0.14em] uppercase text-[#1D4ED8] mb-2">{hero.overline}</div>}
      <h1 className="text-xl font-bold text-[#0A0F1C] leading-tight mb-2">
        {hero.headline_a || 'Headline part A'}{' '}
        <span className="text-[#1D4ED8]">{hero.headline_b || 'blue'}</span>{' '}
        <span className="text-[#F97316]">{hero.headline_c || 'orange'}</span>
      </h1>
      <p className="text-xs text-[#475569] mb-3 leading-relaxed">{hero.sub || 'Sub-headline preview...'}</p>
      <div className="flex flex-col gap-2">
        <div className="bg-[#F97316] text-white text-[11px] font-bold rounded px-3 py-2 text-center truncate">{hero.primary_cta_label || 'Primary CTA'}</div>
        <div className="border border-[#0A0F1C] text-[#0A0F1C] text-[11px] font-bold rounded px-3 py-2 text-center truncate">{hero.secondary_cta_label || 'Secondary CTA'}</div>
      </div>
    </div>
  );
}

function FinalCtaPreview({ cta = {} }) {
  return (
    <div className="bg-[#1D4ED8] p-4 text-center text-white">
      {cta.urgency_line && <div className="text-[9px] font-bold tracking-[0.14em] uppercase text-[#BFDBFE] mb-2">{cta.urgency_line}</div>}
      <h2 className="text-lg font-bold leading-tight mb-2">{cta.headline || 'Headline'}</h2>
      <p className="text-xs text-[#BFDBFE] mb-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: cta.body || 'Body preview...' }} />
      {cta.sub_body && <p className="text-[11px] text-[#BFDBFE] mb-3 leading-relaxed">{cta.sub_body}</p>}
      <div className="bg-[#F97316] text-white text-[11px] font-bold rounded px-3 py-2 inline-block truncate max-w-full">{cta.primary_cta_label || 'CTA'}</div>
    </div>
  );
}

function BrandExplainerPreview({ text }) {
  return (
    <div className="bg-white p-4 text-center">
      <div className="text-[9px] font-bold tracking-[0.14em] uppercase text-[#1D4ED8] mb-2">Brand Ecosystem</div>
      <h2 className="text-base font-bold text-[#0A0F1C] mb-2">A Portfolio of Purpose-Driven Ventures</h2>
      <p className="text-xs text-[#475569] leading-relaxed">{text || 'Explainer preview...'}</p>
    </div>
  );
}

function FooterPreview({ footer = {} }) {
  return (
    <div className="bg-[#0F172A] p-4 text-left text-white">
      {footer.overline && <div className="text-[9px] font-bold tracking-[0.14em] uppercase text-[#FB923C] mb-2">{footer.overline}</div>}
      <h3 className="text-base font-bold mb-2">{footer.headline || 'Headline'}</h3>
      <p className="text-xs text-[#94A3B8] leading-relaxed">{footer.body || 'Body preview...'}</p>
    </div>
  );
}

function VideoPreview({ video = {} }) {
  if (!video.enabled) {
    return <div className="bg-white p-6 text-center text-xs text-[#94A3B8]">Section hidden. Enable the checkbox on the left to show it on the homepage.</div>;
  }
  return (
    <div className="bg-white p-4 text-center">
      <div className="text-[9px] font-bold tracking-[0.14em] uppercase text-[#1D4ED8] mb-2">A Personal Message from David</div>
      <h2 className="text-base font-bold text-[#0A0F1C] mb-2 leading-tight">{video.heading || 'Heading'}</h2>
      {video.sub && <p className="text-xs text-[#475569] mb-3 leading-relaxed">{video.sub}</p>}
      <div className="aspect-video bg-[#0F172A] rounded flex items-center justify-center text-[10px] text-[#94A3B8]">
        {video.video_url ? 'Video URL set' : 'No video URL'}
      </div>
    </div>
  );
}

function FeaturedInPreview({ data = {} }) {
  const logos = Array.isArray(data.logos) ? data.logos : [];
  if (!data.enabled || logos.length === 0) {
    return <div className="bg-white p-6 text-center text-xs text-[#94A3B8]">Section hidden. Enable the checkbox and add at least one logo to show on the homepage.</div>;
  }
  return (
    <div className="bg-white p-4">
      <div className="text-[9px] font-bold tracking-[0.18em] uppercase text-[#1D4ED8] mb-2 text-center">{data.overline || 'Featured In / As Seen In'}</div>
      {data.headline && <div className="text-sm font-bold text-[#0A0F1C] mb-3 text-center">{data.headline}</div>}
      <div className="flex flex-wrap items-center justify-center gap-4">
        {logos.slice(0, 6).map((l, i) => (
          l.logo_url ? (
            <img key={i} src={l.logo_url.startsWith('http') ? l.logo_url : `${API_URL}${l.logo_url}`} alt={l.label || ''} className="h-7 w-auto object-contain grayscale opacity-70" />
          ) : (
            <span key={i} className="text-xs font-bold text-[#475569]/70">{l.label || 'Logo'}</span>
          )
        ))}
      </div>
    </div>
  );
}

function Field({ label, v, onChange, textarea, type }) {
  return (
    <div>
      <div className="text-xs font-bold text-[#475569] uppercase tracking-wider mb-1">{label}</div>
      {textarea ? (
        <textarea rows={3} value={v || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:border-[#1D4ED8]" />
      ) : (
        <input type={type || 'text'} value={v ?? ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:border-[#1D4ED8]" />
      )}
    </div>
  );
}

function Stat({ label, v, extra, accent }) {
  const color = accent === 'red' ? 'text-red-600' : 'text-[#1D4ED8]';
  return (
    <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
      <div className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">{label}</div>
      <div className={`text-3xl font-bold ${color}`}>{v}</div>
      {extra && <div className="text-xs text-[#94A3B8] mt-0.5">{extra}</div>}
    </div>
  );
}
